// The cinematic 3D backdrop for the title screen: the Flying Saucer teahouse
// drifting through a nebula, with a ringed planet, tractor beam and orbiting teacups.
import { Scene, Camera, Node, Light } from '../engine/renderer.js';
import { GeoBuilder, m } from '../engine/geometry.js';
import { col, lerp, angleLerp, rand } from '../engine/math.js';
import * as M from '../game/models.js';

export class MenuScene {
  constructor() {
    const s = (this.scene = new Scene());
    this.camera = new Camera();
    this.camera.fov = (38 * Math.PI) / 180;
    this.t = 0;
    this.mode = 'title'; // title | map | crew | book
    this.mx = 0;
    this.my = 0;
    this.smx = 0;
    this.smy = 0;
    const e = s.env;
    e.sunDir = [-0.7, -0.35, 0.6];
    e.sunColor = col('#ffd8b0');
    e.sunIntensity = 3.2;
    e.skyAmb = col('#3a2a70');
    e.groundAmb = col('#140a24');
    e.rim = col('#7ad8ff');
    e.rimIntensity = 0.7;
    e.envIntensity = 1.3;
    e.exposure = 1.1;
    e.bloom = 1.35;
    e.bloomThreshold = 0.9;
    e.vignette = 0.5;
    e.aberration = 0.02;
    e.grain = 0.035;
    e.saturation = 1.12;
    e.lift = [0.02, 0.0, 0.05];
    e.gain = [1.03, 1.0, 1.02];
    e.reflections = false;
    e.shadowCenter = [0, 0, 0];
    e.shadowRadius = 6;
    s.sky.neb1 = col('#5b2a96');
    s.sky.neb2 = col('#23c4e0');
    s.sky.neb3 = col('#ff6fb5');
    s.sky.space = col('#040316');
    s.sky.starDir = [0.75, 0.3, -0.6];
    s.sky.starCol = col('#ffd9a8').map((v) => v * 1.4);
    s.sky.planetDir = [-0.75, 0.14, -0.62];
    s.sky.planetSize = 0.17;
    s.sky.planetCol = col('#4a2f8a');
    s.sky.planetCol2 = col('#f7a8d4');
    s.sky.ring = 1;
    s.sky.nebIntensity = 1.35;
    s.sky.seed = 3.3;
    this._buildSaucer();
    this._buildExtras();
  }

  _buildSaucer() {
    const s = this.scene;
    const saucer = (this.saucer = new Node());
    s.add(saucer);
    const hullTop = m('#dcd6ea', { rough: 0.25, metal: 0.85 });
    const hullBot = m('#3a3450', { rough: 0.35, metal: 0.9 });
    const b = new GeoBuilder();
    // lower hull
    b.lathe([[0.001, -0.75], [0.9, -0.7], [2.4, -0.35], [3.3, -0.05], [3.45, 0.02]], 72, hullBot);
    // upper hull
    b.lathe([[3.45, 0.02], [3.3, 0.14], [2.6, 0.32], [1.9, 0.42], [1.7, 0.44]], 72, hullTop);
    // deck floor inside the dome
    b.disc(1.72, m('#2a2240', { rough: 0.3, metal: 0.2 }), { p: [0, 0.44, 0] }, 48);
    b.torus(1.72, 0.05, m('#ffcf7a', { rough: 0.25, metal: 1 }), { p: [0, 0.45, 0] }, 72, 6);
    // rim band
    b.torus(3.42, 0.07, m('#8a86a8', { rough: 0.2, metal: 1 }), { p: [0, 0.02, 0] }, 96, 8);
    // underside glow ring
    b.torus(1.0, 0.07, m('#7af0ff', { emit: 6 }), { p: [0, -0.7, 0] }, 48, 6);
    b.torus(2.3, 0.035, m('#ff8ad8', { emit: 4 }), { p: [0, -0.38, 0] }, 72, 4);
    b.cylinder(0.55, 0.8, 0.25, m('#2a2438', { rough: 0.3, metal: 0.9 }), { p: [0, -0.8, 0] }, 32);
    b.disc(0.55, m('#bff8ff', { emit: 5 }), { p: [0, -0.93, 0], s: [1, -1, 1] }, 32);
    // landing legs
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + 0.5;
      b.cylinder(0.04, 0.05, 0.6, m('#8a86a8', { rough: 0.2, metal: 1 }), { p: [Math.cos(a) * 1.8, -0.75, Math.sin(a) * 1.8], r: [Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3] }, 8);
      b.sphere(0.09, m('#ffcf7a', { emit: 2 }), { p: [Math.cos(a) * 1.95, -1.05, Math.sin(a) * 1.95] });
    }
    // interior: tiny tables, stools, bar, plants, lamps
    const inside = new GeoBuilder();
    const bar = m('#f5efe6', { rough: 0.2 });
    inside.lathe([[0.001, 0.44], [0.55, 0.44], [0.55, 0.78], [0.001, 0.78]], 40, m('#a0714a', { rough: 0.45 }));
    inside.cylinder(0.6, 0.6, 0.05, bar, { p: [0, 0.8, 0] }, 40);
    inside.torus(0.6, 0.015, m('#ffcf7a', { rough: 0.25, metal: 1 }), { p: [0, 0.8, 0] }, 40, 4);
    inside.lathe([[0.001, 0.82], [0.12, 0.82], [0.16, 0.9], [0.1, 0.98], [0.001, 1.0]], 16, m('#fbf8ff', { rough: 0.18 }));
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const r = 1.2;
      inside.cylinder(0.2, 0.2, 0.03, m('#f5efe6', { rough: 0.2 }), { p: [Math.cos(a) * r, 0.72, Math.sin(a) * r] }, 18);
      inside.cylinder(0.025, 0.025, 0.28, m('#e8ecf5', { rough: 0.12, metal: 1 }), { p: [Math.cos(a) * r, 0.58, Math.sin(a) * r] }, 6);
      inside.lathe([[0.001, 0.735], [0.04, 0.735], [0.05, 0.79], [0.001, 0.77]], 10, m('#fbf8ff', { rough: 0.2 }), { p: [Math.cos(a) * r + 0.06, 0, Math.sin(a) * r] });
      for (const off of [-0.32, 0.32]) {
        const aa = a + off;
        inside.cylinder(0.1, 0.1, 0.05, m(i % 2 ? '#ff8ad8' : '#7af0ff', { rough: 0.6, emit: 0.2 }), { p: [Math.cos(aa) * (r + 0.1), 0.62, Math.sin(aa) * (r + 0.1)] }, 12);
      }
      // tiny glowing plant
      const pa = a + Math.PI / 6;
      inside.sphere(0.11, m('#3ab07a', { rough: 0.5 }), { p: [Math.cos(pa) * 1.55, 0.58, Math.sin(pa) * 1.55], s: [1, 1.4, 1] }, 10, 8);
      inside.sphere(0.035, m(i % 2 ? '#ffd35a' : '#ff8ad8', { emit: 6 }), { p: [Math.cos(pa) * 1.55, 0.75, Math.sin(pa) * 1.55] }, 8, 6);
      // hanging lamp glow discs
      inside.sphere(0.07, m('#ffe0b0', { emit: 9 }), { p: [Math.cos(a) * r, 1.35, Math.sin(a) * r] }, 10, 8);
      inside.cylinder(0.004, 0.004, 0.5, m('#222', {}), { p: [Math.cos(a) * r, 1.62, Math.sin(a) * r] }, 4);
    }
    saucer.add(new Node(M.meshFrom(b)));
    saucer.add(new Node(M.meshFrom(inside)));
    this._buildGuests(saucer);
    // glass dome
    const dome = new GeoBuilder();
    dome.hemisphere(1.72, m('#bfe8ff'), { p: [0, 0.44, 0], s: [1, 0.78, 1] }, 48, 20);
    const domeNode = new Node(M.meshFrom(dome), { fx: { mode: 1 }, castShadow: false });
    domeNode.tint = [0.55, 0.8, 1.1, 1.0];
    saucer.add(domeNode);
    this.dome = domeNode;
    // spire and beacon on top of the dome
    const spire = new GeoBuilder();
    spire.cylinder(0.02, 0.05, 0.5, m('#e8ecf5', { rough: 0.12, metal: 1 }), { p: [0, 1.95, 0] }, 8);
    spire.torus(0.12, 0.02, m('#ffcf7a', { rough: 0.2, metal: 1 }), { p: [0, 1.78, 0] }, 20, 5);
    saucer.add(new Node(M.meshFrom(spire)));
    this.beacon = new Node(M.sphereMesh('#ff5ab0', 8));
    this.beacon.setScale(0.07);
    this.beacon.position = [0, 2.22, 0];
    saucer.add(this.beacon);
    // rim lights (rotating ring)
    const rim = new GeoBuilder();
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      const c = ['#7af0ff', '#ff8ad8', '#ffd35a'][i % 3];
      rim.sphere(0.075, m(c, { emit: 7 }), { p: [Math.cos(a) * 3.47, 0.02, Math.sin(a) * 3.47] }, 8, 6);
    }
    // window strip on upper hull
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      rim.sphere(0.06, m('#ffd9a0', { emit: 5 }), { p: [Math.cos(a) * 2.75, 0.265, Math.sin(a) * 2.75] }, 8, 6);
    }
    this.rim = new Node(M.meshFrom(rim));
    saucer.add(this.rim);
    // lights inside the dome
    this.domeLights = [];
    const addL = (o) => {
      const l = new Light(o);
      s.lights.push(l);
      return l;
    };
    this.domeLights.push(addL({ position: [0, 1.4, 0], color: '#ffd6a0', intensity: 10, range: 3.5 }));
    this.domeLights.push(addL({ position: [1.0, 1.0, 0.8], color: '#ff8ad8', intensity: 4, range: 2.5 }));
    this.domeLights.push(addL({ position: [-1.0, 1.0, -0.6], color: '#7af0ff', intensity: 4, range: 2.5 }));
    this.underLight = addL({ position: [0, -1.4, 0], color: '#7af0ff', intensity: 14, range: 4.5 });
    this.rimLight = addL({ position: [0, 0.3, 3.8], color: '#ff8ad8', intensity: 6, range: 4 });
    this.beaconLight = addL({ position: [0, 2.3, 0], color: '#ff5ab0', intensity: 3, range: 3 });
    // tractor beam
    this.beam = new Node(M.beamMesh(1.4, 5.5), { fx: { mode: 2, param: 5.5 }, castShadow: false });
    this.beam.position = [0, -3.7, 0];
    this.beam.tint = [0.45, 0.95, 1.2, 0.2];
    saucer.add(this.beam);
  }

  /** The teahouse's regulars, a third of their in-game size: some sit at the tables chatting and sipping, two stroll the aisle. */
  _buildGuests(saucer) {
    const TABLE_R = 1.2, STOOL_R = 1.3, SEAT_Y = 0.63, DECK_Y = 0.445, SCALE = 0.3;
    const species = ['glorb', 'zeeb', 'nuvari', 'krix', 'moff'];
    const drinks = ['jasmine_tea', 'mango_magic', 'glowing_green', 'choco_latte', 'bliss_bubbler', 'cosmic_milk_tea', 'supernova', 'void_bloom'];
    const alien = (i) => {
      const parts = M.buildAlien(species[i % species.length]);
      parts.root.setScale(SCALE);
      saucer.add(parts.root);
      return parts;
    };
    this.guests = [];
    // [table, stool side]: two tables host a pair each, the rest a single guest
    const seats = [[0, -1], [0, 1], [1, 1], [2, -1], [3, -1], [3, 1], [4, 1], [5, -1]];
    seats.forEach(([ti, side], i) => {
      const parts = alien(i);
      const ta = (ti / 6) * Math.PI * 2;
      const sa = ta + side * 0.32;
      const x = Math.cos(sa) * STOOL_R, z = Math.sin(sa) * STOOL_R;
      const tx = Math.cos(ta) * TABLE_R, tz = Math.sin(ta) * TABLE_R;
      // a teacup with a real drink from the menu, held like a served guest holds it
      const cup = new Node();
      const body = new Node();
      body.setScale(1.2);
      body.add(new Node(M.cupMesh(false)));
      body.add(new Node(M.drinkMesh(drinks[i % drinks.length])));
      cup.add(body);
      cup.position = [0.22, parts.species.id === 'zeeb' ? 0.55 : 0.4, 0.3];
      parts.body.add(cup);
      this.guests.push({ parts, cup, x, z, y: SEAT_Y, face: Math.atan2(tx - x, tz - z), seated: true, phase: i * 1.7, blink: 1 + i * 0.4, sipT: 2 + ((i * 2.3) % 5), laughT: 3 + ((i * 1.9) % 6), happy: 0, sip: 0 });
    });
    // two strollers doing laps of the aisle between the bar and the tables
    for (let k = 0; k < 2; k++) {
      const parts = alien(k + 2);
      this.guests.push({ parts, seated: false, a: k * Math.PI, dir: k ? -1 : 1, r: 0.86, y: DECK_Y, phase: k * 2.4, blink: 1.5 + k, walkT: 0, pauseT: 4 + k * 3, laughT: 5 + k * 2, happy: 0 });
    }
  }

  _animateGuests(dt, t) {
    for (const g of this.guests) {
      const p = g.parts;
      let hop = 0, yaw;
      if (g.seated) {
        // chat: turn a little this way and that, now and then a happy bounce
        yaw = g.face + Math.sin(t * 0.7 + g.phase) * 0.45;
        g.sipT -= dt;
        if (g.sipT <= 0) {
          g.sip = 1;
          g.sipT = 4 + ((g.phase * 3.1) % 5);
        }
        g.sip = Math.max(0, g.sip - dt * 0.6);
        // a sip: lift the cup and tip it, like a served guest drinking
        const s = Math.sin(g.sip * Math.PI);
        g.cup.rotation[2] = s * 0.9;
        g.cup.position[1] = (p.species.id === 'zeeb' ? 0.55 : 0.4) + s * 0.25;
        p.root.position = [g.x, g.y, g.z];
      } else {
        // stroll the aisle, stopping at the bar every so often
        g.pauseT -= dt;
        const walking = g.pauseT > 0;
        if (g.pauseT < -2.2) g.pauseT = 5 + ((g.phase * 4.7) % 4);
        if (walking) {
          g.a += dt * 0.32 * g.dir;
          g.walkT += dt;
          hop = Math.abs(Math.sin(g.walkT * 9)) * 0.12;
        }
        const x = Math.cos(g.a) * g.r, z = Math.sin(g.a) * g.r;
        // walking: face along the aisle; stopped: face the bar in the middle
        yaw = walking ? Math.atan2(-Math.sin(g.a) * g.dir, Math.cos(g.a) * g.dir) : Math.atan2(-x, -z);
        p.root.position = [x, g.y + hop * 0.3, z];
      }
      p.root.rotation[1] = angleLerp(p.root.rotation[1], yaw, 1 - Math.exp(-dt * 6));
      g.laughT -= dt;
      if (g.laughT <= 0) {
        g.happy = 1;
        g.laughT = 4 + ((g.phase * 5.3) % 7);
      }
      g.happy = Math.max(0, g.happy - dt * 1.5);
      const happyS = g.happy > 0 ? 1 + Math.sin(g.happy * Math.PI) * 0.15 : 1;
      const breathe = 1 + Math.sin(t * 2.2 + g.phase) * 0.03;
      const floaty = p.species.id === 'nuvari' ? (0.15 + Math.sin(t * 1.8 + g.phase) * 0.06) * 0.3 : 0;
      p.root.position[1] += floaty + (g.happy > 0 ? Math.sin(g.happy * Math.PI) * 0.03 : 0);
      p.body.scale = [happyS, breathe * happyS, happyS];
      // blink
      g.blink -= dt;
      const eyeS = g.blink < 0.12 ? 0.1 : 1;
      if (g.blink < 0) g.blink = 2 + ((g.phase * 7.7) % 3);
      for (const e of p.eyes) e.scale[1] = e.scale[0] * eyeS;
      // everyone here is having a lovely time
      p.mouth.rotation[2] = p.asset ? 0 : Math.PI;
    }
  }

  _buildExtras() {
    const s = this.scene;
    // orbiting teacups
    this.cups = [];
    const colors = ['#f0e6ff', '#ffb347', '#6dff9e', '#ff9ab0', '#9ffcff', '#c8ff5a'];
    for (let i = 0; i < 6; i++) {
      const n = new Node();
      const cup = new Node(M.cupMesh(false));
      n.add(cup);
      const liq = new Node(M.liquidMesh());
      liq.position = [0, 0.13, 0];
      const c = col(colors[i]);
      liq.tint = [...c, 1];
      liq.emissive = c.map((v) => v * 0.8);
      n.add(liq);
      n.setScale(1.6);
      s.add(n);
      this.cups.push({ node: n, a: (i / 6) * Math.PI * 2, r: 4.4 + (i % 2) * 0.8, y: -0.2 + (i % 3) * 0.5, sp: 0.12 + (i % 3) * 0.03, c });
    }
    // asteroids
    this.rocks = [];
    const rockB = new GeoBuilder();
    rockB.sphere(1, m('#5a4a6a', { rough: 0.85 }), { s: [1, 0.8, 0.9] }, 7, 5);
    rockB.sphere(0.6, m('#4a3a5a', { rough: 0.9 }), { p: [0.5, 0.3, 0.2] }, 6, 4);
    const rockMesh = M.meshFrom(rockB);
    for (let i = 0; i < 14; i++) {
      const n = new Node(rockMesh);
      const a = rand(0, Math.PI * 2);
      const r = rand(14, 24);
      n.position = [Math.cos(a) * r, rand(-5, 4), Math.sin(a) * r - 8];
      n.setScale(rand(0.1, 0.45));
      n.rotation = [rand(0, 6), rand(0, 6), rand(0, 6)];
      s.add(n);
      this.rocks.push({ node: n, spin: [rand(-0.3, 0.3), rand(-0.3, 0.3), rand(-0.3, 0.3)] });
    }
  }

  setMouse(x, y) {
    this.mx = x;
    this.my = y;
  }

  update(dt) {
    this.t += dt;
    const t = this.t;
    const s = this.scene;
    this.smx = lerp(this.smx, this.mx, 1 - Math.exp(-dt * 2));
    this.smy = lerp(this.smy, this.my, 1 - Math.exp(-dt * 2));
    // saucer drifts
    const sa = this.saucer;
    sa.position = [0, Math.sin(t * 0.6) * 0.18, 0];
    sa.rotation = [Math.sin(t * 0.4) * 0.05, t * 0.08, Math.cos(t * 0.33) * 0.05];
    this.rim.rotation[1] = t * 0.5;
    this.beacon.emitMul = 0.5 + Math.max(0, Math.sin(t * 3)) * 1.5;
    this.beaconLight.intensity = 1 + Math.max(0, Math.sin(t * 3)) * 5;
    this.beam.tint[3] = 0.16 + Math.sin(t * 2) * 0.04;
    this.beam.rotation[1] = t * 0.4;
    this.underLight.intensity = 12 + Math.sin(t * 2) * 2;
    this.domeLights[0].intensity = 10 + Math.sin(t * 7) * 0.4;
    // saucer world position for lights (they're in world space, saucer moves slightly)
    const y = sa.position[1];
    this.domeLights[0].position[1] = 1.4 + y;
    this.underLight.position[1] = -1.4 + y;
    this.beaconLight.position[1] = 2.3 + y;
    // beam sparkles
    if (Math.random() < dt * 30) {
      const a = rand(0, 6.28), r = rand(0, 1.2);
      s.particles[0].emit({ pos: [Math.cos(a) * r, y - 6, Math.sin(a) * r], vel: [0, rand(1.2, 2.5), 0], life: rand(2, 3.5), size: rand(0.02, 0.05), size1: 0.0, color: col(Math.random() < 0.5 ? '#9ff6ff' : '#ffffff'), alpha: 1.5, type: 1 });
    }
    // cups orbit with steam
    for (const c of this.cups) {
      c.a += dt * c.sp;
      const px = Math.cos(c.a) * c.r, pz = Math.sin(c.a) * c.r;
      const py = c.y + Math.sin(t * 0.9 + c.a * 3) * 0.3 + y;
      c.node.position = [px, py, pz];
      c.node.rotation = [Math.sin(t + c.a) * 0.3, -c.a, Math.cos(t * 0.7 + c.a) * 0.3];
      if (Math.random() < dt * 5) s.particles[1].emit({ pos: [px, py + 0.35, pz], vel: [rand(-0.05, 0.05), rand(0.25, 0.45), rand(-0.05, 0.05)], life: 2.2, size: 0.05, size1: 0.28, color: [1, 1, 1], alpha: 0.28, type: 2, drag: 0.5 });
      if (Math.random() < dt * 3) s.particles[0].emit({ pos: [px, py, pz], vel: [0, -0.05, 0], life: 1.6, size: 0.04, size1: 0, color: c.c, alpha: 1.5, type: 1 });
    }
    this._animateGuests(dt, t);
    for (const r of this.rocks) {
      r.node.rotation[0] += r.spin[0] * dt;
      r.node.rotation[1] += r.spin[1] * dt;
      r.node.rotation[2] += r.spin[2] * dt;
    }
    // shooting stars
    if (Math.random() < dt * 0.35) {
      const sx = rand(-20, 20), sy = rand(4, 10), sz = rand(-25, -15);
      const v = [rand(-14, -8), rand(-4, -2), rand(-1, 1)];
      for (let i = 0; i < 18; i++) s.particles[0].emit({ pos: [sx - v[0] * i * 0.012, sy - v[1] * i * 0.012, sz], vel: v, life: 0.9 - i * 0.03, size: 0.12 - i * 0.005, size1: 0, color: [1, 0.9, 0.8], alpha: 2, type: 0 });
    }
    s.particles.forEach((p) => p.update(dt));
    // camera
    const cam = this.camera;
    const modes = {
      title: { r: 9.5, h: 2.2, yaw: 0.35, tx: 2.2, ty: 0.1 },
      map: { r: 12, h: 1.5, yaw: -0.3, tx: 0, ty: 5.2 },
      crew: { r: 7.5, h: 1.2, yaw: 0.9, tx: -1.6, ty: 0.3 },
      book: { r: 8.5, h: 3, yaw: -1.4, tx: 2.5, ty: 0.4 },
      settings: { r: 10, h: -1.5, yaw: 1.8, tx: -2, ty: 0 },
    };
    const md = modes[this.mode] || modes.title;
    this._cm = this._cm || { ...md };
    for (const k in md) this._cm[k] = lerp(this._cm[k], md[k], 1 - Math.exp(-dt * 2.2));
    const cm = this._cm;
    const yaw = cm.yaw + Math.sin(t * 0.07) * 0.15 + this.smx * 0.12;
    const h = cm.h + this.smy * 0.6 + Math.sin(t * 0.11) * 0.3;
    cam.position = [Math.sin(yaw) * cm.r, h, Math.cos(yaw) * cm.r];
    // offset target sideways so the saucer sits right of the menu
    const right = [Math.cos(yaw), 0, -Math.sin(yaw)];
    cam.target = [-right[0] * cm.tx, cm.ty, -right[2] * cm.tx];
  }
}
